import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DropdownMenu, { DropdownMenuItem } from '../../../components/DropdownMenu/DropdownMenu';
import './CreateFilmModal.css';

interface CreateFilmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (filmData: any) => void;
}

const initialFilmData = {
  filmName: '',
  filmType: '电影', // 默认为"电影"
  filmProjectType: '预算项目' as '分场项目' | '预算项目', // 默认为"预算项目"
  executiveProducer: '',
  director: '',
  screenwriter: '',
  filmSynopsis: ''
};

// 影片类型 id 为中文（与 API / 存量数据一致），展示文案走 film.filmTypes.*
const FILM_TYPE_IDS = ['电影', '电视剧', '动漫', '短剧', '纪录片', '微电影', '短片', '广告'] as const;
const filmTypeKeyMap: Record<string, string> = {
  电影: 'movie',
  电视剧: 'tv',
  动漫: 'anime',
  短剧: 'short',
  纪录片: 'documentary',
  微电影: 'micro',
  短片: 'shortFilm',
  广告: 'ad',
};

const CreateFilmModal: React.FC<CreateFilmModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const [filmData, setFilmData] = useState(initialFilmData);

  const filmTypeItems: DropdownMenuItem[] = useMemo(
    () =>
      FILM_TYPE_IDS.map((type) => ({
        id: type,
        label: t(`film.filmTypes.${filmTypeKeyMap[type]}`),
      })),
    [t]
  );
  
  // 影片类型下拉菜单相关状态
  const [filmTypeMenuVisible, setFilmTypeMenuVisible] = useState(false);
  const filmTypeButtonRef = useRef<HTMLButtonElement>(null);
  const [filmTypeMenuPosition, setFilmTypeMenuPosition] = useState({ top: 0, left: 0 });
  const [filmTypeMenuWidth, setFilmTypeMenuWidth] = useState<number | undefined>(undefined);

  // 监听 isOpen 变化，当模态框关闭时重置表单
  useEffect(() => {
    if (!isOpen) {
      setFilmData(initialFilmData);
      setFilmTypeMenuVisible(false);
    }
  }, [isOpen]);

  // 更新影片类型下拉菜单位置和宽度
  useEffect(() => {
    if (filmTypeMenuVisible && filmTypeButtonRef.current) {
      const rect = filmTypeButtonRef.current.getBoundingClientRect();
      setFilmTypeMenuPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
      setFilmTypeMenuWidth(rect.width);
    }
  }, [filmTypeMenuVisible]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFilmData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理影片类型选择
  const handleFilmTypeSelect = (item: DropdownMenuItem) => {
    setFilmData(prev => ({
      ...prev,
      filmType: item.id as string
    }));
    setFilmTypeMenuVisible(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(filmData);
  };

  // 处理取消按钮点击
  const handleCancel = () => {
    setFilmData(initialFilmData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('createFilmModal.title')}</h2>
          <button className="close-button" onClick={handleCancel}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-container">
              <div className="form-group">
                <label>{t('film.filmName')}</label>
                <input
                  type="text"
                  name="filmName"
                  value={filmData.filmName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group create-film-modal-row">
                <div className="form-group create-film-modal-row-left">
                  <label>{t('film.filmProjectType')}</label>
                  <div className="create-film-modal-tabs">
                    {(
                      [
                        ['预算项目', 'createFilmModal.budgetProject'],
                        ['分场项目', 'createFilmModal.breakdownProject'],
                      ] as const
                    ).map(([id, i18nKey]) => (
                      <button
                        key={id}
                        type="button"
                        className={`create-film-modal-tab ${filmData.filmProjectType === id ? 'active' : ''}`}
                        onClick={() => setFilmData((prev) => ({ ...prev, filmProjectType: id }))}
                      >
                        {t(i18nKey)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group create-film-modal-row-right">
                  <label>{t('film.filmType')}</label>
                  <button
                    ref={filmTypeButtonRef}
                    type="button"
                    className="form-group-select-button form-group-select-button-narrow"
                    onClick={() => setFilmTypeMenuVisible(!filmTypeMenuVisible)}
                  >
                    {filmData.filmType && filmTypeKeyMap[filmData.filmType]
                      ? t(`film.filmTypes.${filmTypeKeyMap[filmData.filmType]}`)
                      : t('film.filmTypes.movie')}
                  </button>
                  <DropdownMenu
                    items={filmTypeItems}
                    visible={filmTypeMenuVisible}
                    onClose={() => setFilmTypeMenuVisible(false)}
                    onSelect={handleFilmTypeSelect}
                    position={filmTypeMenuPosition}
                    width={filmTypeMenuWidth}
                    useFixedPosition={true}
                    selectedValue={filmData.filmType}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('film.executiveProducer')}</label>
                <input
                  type="text"
                  name="executiveProducer"
                  value={filmData.executiveProducer}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>{t('film.director')}</label>
                <input
                  type="text"
                  name="director"
                  value={filmData.director}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>{t('film.screenwriter')}</label>
                <input
                  type="text"
                  name="screenwriter"
                  value={filmData.screenwriter}
                  onChange={handleInputChange}
                />
              </div>
          </div>
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCancel}>
              {t('createFilmModal.cancel')}
            </button>
            <button type="submit" className="submit-button">
              {t('createFilmModal.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFilmModal;
